import { useEffect, useCallback } from "react";
import type { CalendarEvent, MeetingRoom, Attendee } from "@/types/calendar";
import { formatTimeShort } from "@/lib/calendar-utils";
import { cn } from "@/lib/utils";

interface EventDetailModalProps {
  event: CalendarEvent | null;
  room: MeetingRoom | null;
  onClose: () => void;
}

// Format email for display (show name or shortened email)
function formatEmailDisplay(email: string): string {
  // If it looks like an email, show just the username part
  if (email.includes("@")) {
    return email.split("@")[0];
  }
  return email;
}

// Attendee chip component with status indicator
function AttendeeChip({ attendee }: { attendee: Attendee }) {
  const displayName = attendee.name || formatEmailDisplay(attendee.email);

  const statusStyles: Record<Attendee["status"], string> = {
    accepted: "bg-green-500/20 text-green-700 dark:text-green-400",
    declined:
      "bg-red-500/20 text-red-700 dark:text-red-400 line-through opacity-60",
    tentative: "bg-yellow-500/20 text-yellow-700 dark:text-yellow-400",
    "needs-action": "bg-muted text-muted-foreground",
  };

  const statusIcons: Record<Attendee["status"], string> = {
    accepted: "✓",
    declined: "✗",
    tentative: "?",
    "needs-action": "·",
  };

  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs",
        statusStyles[attendee.status],
      )}
      title={`${attendee.email} (${attendee.status})`}
    >
      <span className="text-[10px]">{statusIcons[attendee.status]}</span>
      {displayName}
    </span>
  );
}

export function EventDetailModal({
  event,
  room,
  onClose,
}: EventDetailModalProps) {
  // Handle Escape key
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    },
    [onClose],
  );

  useEffect(() => {
    if (event) {
      document.addEventListener("keydown", handleKeyDown);
      // Prevent body scroll when modal is open
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
    };
  }, [event, handleKeyDown]);

  if (!event || !room) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end md:items-center justify-center"
      onClick={onClose}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />

      {/* Modal content - bottom sheet on mobile, centered on desktop */}
      <div
        className={cn(
          "relative w-full md:w-auto md:min-w-[320px] md:max-w-md",
          "max-h-[80vh] flex flex-col",
          "bg-background rounded-t-2xl md:rounded-xl",
          "shadow-xl",
          "animate-in fade-in duration-200",
          // Mobile: slide up from bottom
          "slide-in-from-bottom-4 md:slide-in-from-bottom-0 md:zoom-in-95",
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Color header bar */}
        <div
          className="h-2 rounded-t-2xl md:rounded-t-xl"
          style={{ backgroundColor: room.color }}
        />

        {/* Drag handle (mobile only) */}
        <div className="flex justify-center pt-2 md:hidden">
          <div className="w-10 h-1 bg-muted-foreground/30 rounded-full" />
        </div>

        {/* Close button */}
        <button
          onClick={onClose}
          className={cn(
            "absolute top-3 right-3 md:top-4 md:right-4",
            "w-8 h-8 flex items-center justify-center",
            "rounded-full bg-muted/50 hover:bg-muted",
            "text-muted-foreground hover:text-foreground",
            "transition-colors",
          )}
          aria-label="Close"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        {/* Content */}
        <div className="p-4 md:p-6 pt-2 md:pt-4 overflow-y-auto flex-1 min-h-0">
          {/* Room name */}
          <div
            className="text-xs font-medium uppercase tracking-wide mb-2"
            style={{ color: room.color }}
          >
            {room.displayName}
          </div>

          {/* Event title */}
          <h2 className="text-lg md:text-xl font-semibold text-foreground mb-3 pr-8">
            {event.summary}
          </h2>

          {/* Time */}
          <div className="flex items-center gap-2 text-muted-foreground mb-3">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="12" cy="12" r="10" />
              <polyline points="12 6 12 12 16 14" />
            </svg>
            <span>
              {formatTimeShort(event.start)} - {formatTimeShort(event.end)}
            </span>
          </div>

          {/* Organizer */}
          {event.organizer && (
            <div className="flex items-center gap-2 text-muted-foreground text-sm mb-3 min-w-0">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="shrink-0"
              >
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>
              <span className="truncate">
                <span className="text-muted-foreground/70">주최:</span>{" "}
                {event.organizer}
              </span>
            </div>
          )}

          {/* Attendees */}
          {event.attendees && event.attendees.length > 0 && (
            <div className="mb-3">
              <div className="flex items-start gap-2 text-muted-foreground text-sm">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="shrink-0 mt-0.5"
                >
                  <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
                  <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
                <div className="flex-1">
                  <span className="text-muted-foreground/70">
                    참석자 ({event.attendees.length}):
                  </span>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {event.attendees.map((attendee, i) => (
                      <AttendeeChip key={i} attendee={attendee} />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Description (if available) */}
          {event.description && (
            <div className="mt-4 pt-4 border-t border-border">
              <div className="text-xs font-medium text-muted-foreground mb-2 uppercase tracking-wide">
                Description
              </div>
              <p className="text-sm text-foreground whitespace-pre-wrap">
                {event.description}
              </p>
            </div>
          )}

          {/* Location (if available) */}
          {event.location && (
            <div className="mt-3 flex items-start gap-2 text-muted-foreground text-sm min-w-0">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="shrink-0 mt-0.5"
              >
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                <circle cx="12" cy="10" r="3" />
              </svg>
              <span className="break-words">{event.location}</span>
            </div>
          )}
        </div>

        {/* Safe area padding for mobile devices with home indicator */}
        <div className="h-safe-area-inset-bottom md:hidden" />
      </div>
    </div>
  );
}
